import React, { useState, useEffect } from 'react';
import { masterAdminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const ManualRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await masterAdminAPI.getManualRequests();
      setRequests(res.data);
    } catch (error) {
      toast.error('Failed to load manual requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId, approve) => {
    setProcessing(prev => ({ ...prev, [requestId]: true }));
    try {
      const notes = window.prompt(`Enter notes for ${approve ? 'approval' : 'rejection'} (optional):`, '');
      await masterAdminAPI.approveManualRequest({
        request_id: requestId,
        approve,
        admin_notes: notes || '',
      });
      toast.success(`Request ${approve ? 'approved' : 'rejected'} successfully`);
      fetchRequests(); // refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Manual Top-up Requests</h1>

      {requests.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
          No manual requests found.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (₦)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests.map((req) => (
                <tr key={req.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(req.created_at)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {req.school?.name || req.school_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₦{req.amount.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{req.reference}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(req.status)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {req.status === 'pending' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleApprove(req.id, true)}
                          disabled={processing[req.id]}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded disabled:opacity-50"
                        >
                          {processing[req.id] ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleApprove(req.id, false)}
                          disabled={processing[req.id]}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded disabled:opacity-50"
                        >
                          {processing[req.id] ? '...' : 'Reject'}
                        </button>
                      </div>
                    )}
                    {req.status !== 'pending' && req.admin_notes && (
                      <div className="text-xs text-gray-500 max-w-xs truncate" title={req.admin_notes}>
                        Note: {req.admin_notes}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManualRequests;